import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import {
  getListMyOrphanedPaymentsQueryKey,
  useListMyOrphanedPayments,
  useRefundOrphanedPayment,
} from '../../api/generated/endpoints/payments/payments'
import { apiMessage } from '../../lib/api-error'
import { formatUYU } from '../../lib/money'
import { formatDayLong } from '../../lib/time'

/**
 * Money that arrived after the bay had gone.
 *
 * A payment is approved, the fifteen-minute hold had already expired, and
 * somebody else took the slot in between — so the facility is holding money for
 * an hour it cannot supply. This is the screen where that gets undone: pick
 * another time, or ask for the money back.
 *
 * NAMED `payments.index.tsx`, NOT `payments.tsx`, and that is load-bearing.
 * With a sibling `payments.$paymentId.rebook.tsx`, a plain `payments.tsx`
 * becomes a LAYOUT route for it — and a layout with no `<Outlet/>` renders
 * itself in place of its child, so `/payments/:id/rebook` quietly showed this
 * list instead of the picker. Typecheck was clean and every test passed; only
 * opening the URL found it. `bookings.index.tsx` is the same shape for the same
 * reason.
 *
 * A separate screen from `/bookings` because these are not bookings. The
 * booking behind each row is expired and staying that way; what is live is the
 * payment. Folding them into the bookings list would mean a row that looks like
 * a reservation and is the opposite of one.
 *
 * ## With payments off this is an empty page, not an error
 *
 * The endpoint answers `[]` rather than 503, because with payments disabled
 * nothing CAN be orphaned — empty is the truth, not an outage. Rendering
 * `role="alert"` here would fail the browser sweep's no-error-state assertion
 * for a page that simply has nothing to show, which is the bug `/packages`
 * shipped once already.
 */
export const Route = createFileRoute('/_authed/payments/')({
  component: PaymentsPage,
})

function PaymentsPage() {
  const queryClient = useQueryClient()
  // Not destructured: React Query v5's result narrows on `status`, and pulling
  // the fields out kills it. Same note as in book.tsx and me.tsx.
  const orphans = useListMyOrphanedPayments()

  const refund = useRefundOrphanedPayment({
    mutation: {
      retry: false, // a retried refund is a second request for the same money
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getListMyOrphanedPaymentsQueryKey() }),
    },
  })

  if (orphans.isPending) return <p className="text-slate-600">Cargando…</p>
  if (orphans.isError) {
    return (
      <p role="alert" className="text-red-600">
        No se pudo cargar la lista de pagos.
      </p>
    )
  }

  const items = orphans.data.items

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Pagos sin horario</h1>
        <p className="mt-1 text-slate-600">
          Pagos que entraron después de que el horario ya estuviera ocupado.
        </p>
      </div>

      {items.length === 0 ? (
        // Deliberately NOT role="alert". Nothing is wrong; there is nothing here.
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-slate-600">
          No hay pagos sin horario asignado.
        </p>
      ) : (
        <ul className="space-y-4">
          {items.map((item) => (
            <li key={item.id} className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
              <div>
                <p className="text-lg font-semibold tabular-nums">
                  {formatUYU(item.amount_uyu_pesos)}
                </p>
                <p className="mt-1 text-slate-700">
                  El horario del {formatDayLong(item.lost_date)} a las {item.lost_start} en{' '}
                  {item.lost_bay_name} ya estaba ocupado.
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  El importe no se pierde. Se puede elegir otro horario hasta el{' '}
                  {formatDayLong(item.refund_due_at.slice(0, 10))}
                  {item.days_left === 1 ? ' (queda 1 día)' : ` (quedan ${item.days_left} días)`}. Si
                  para entonces no se eligió ninguno, la devolución se hace sola.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/payments/$paymentId/rebook"
                  params={{ paymentId: item.id }}
                  className="inline-flex min-h-12 items-center rounded-lg bg-slate-900 px-4 text-white"
                >
                  Elegir otro horario
                </Link>
                <button
                  type="button"
                  onClick={() => refund.mutate({ paymentId: item.id })}
                  disabled={refund.isPending}
                  className="inline-flex min-h-12 items-center rounded-lg border border-slate-300 px-4 text-slate-900 disabled:opacity-50"
                >
                  Pedir la devolución
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {refund.isError && (
        <p role="alert" className="text-sm text-red-600">
          {apiMessage(refund.error, 'No se pudo pedir la devolución.')}
        </p>
      )}

      {refund.isSuccess && (
        <p role="status" className="text-sm text-slate-700">
          La devolución está en camino. Puede demorar unos días en aparecer en el resumen.
        </p>
      )}

      <Link
        to="/bookings"
        search={{ tab: 'upcoming' }}
        className="inline-flex min-h-12 items-center text-slate-700 underline"
      >
        Volver a las reservas
      </Link>
    </div>
  )
}
